function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Ribbon, Card, Pill, Empty } from "../../components/analytics-kit";
import Auth from "../../functions/authenticate";
import publishSearch from "../../functions/publish-search";
import publishEvent from "../../functions/publish-event";
import { hasTenantFeature } from "../../functions/tenant-features";
import account from "../../data/account";
import { CARDS, ImportWizard, SEED_APPS, askPlunk, ago } from "../data-feeds/connect";
import ServiceFactory from "../../service";
import DataShell, { dataSvc, safe, Btn, Notice } from "./shell";
import Connections from "./connections";
import { feedHealth, lastRealRun } from "./feed-health";

/*
 * Connect (kit, 2026-09-29). Same rails and checklist as views/data-feeds/connect.jsx (which the two
 * legacy-gated tenants keep on prod): feed cards from data_feed_schedule/run, tracking-live probe,
 * setup tasks, the History Import wizard, Seed your apps, and the provider connections registry.
 * Layout: header chips (connected count) → 4-tile Ribbon → one notice (next step + Ask Plunk) →
 * Sources cards → Setup cards → Seed your apps + Ask Plunk → Provider connections table.
 */
const svc = api => dataSvc(`/data-feeds${api}`);
const activitySvc = api => new (ServiceFactory.getType(api))(); // activity root
// Legacy card copy carries em dashes; the kit uses colons.
const clean = t => String(t || "").replace(/\s+—\s+/g, ": ").replace(/—/g, ":");
const TONE = {
  ok: "success",
  warn: "warning",
  info: "info",
  off: "neutral"
};
class ConnectView extends React.Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      providers: null,
      schedules: null,
      runs: [],
      trackingLive: null,
      wizard: false,
      contractOpen: null,
      connections: null,
      seeded: {},
      pointerDismissed: (() => {
        try {
          return localStorage.getItem("plunk-onboarding-askplunk-pointer") === "1";
        } catch (e) {
          return false;
        }
      })()
    });
  }
  async componentDidMount() {
    this._alive = true;
    publishSearch("Connect Your Data");
    publishEvent("reset-search", {});
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    const [providers, schedules, runs, dash, locations, lifecycleRules, configRules] = await Promise.all([safe(svc("/providers").getAsync(), []), safe(svc("/schedules").getAsync(), []), safe(svc("/runs?top=50").getAsync(), []), safe(activitySvc("/dashboard/this_month").getAsync(), null), safe(dataSvc("/location").getAsync(), null), safe(dataSvc("/lifecycle/config").getAsync(), null), safe(dataSvc("/product/configurations/rules").getAsync(), null)]);
    if (!this._alive) return;
    let trackingLive = null;
    if (dash) {
      const nums = [];
      const walk = (o, depth) => {
        if (depth > 3 || o == null) return;
        if (typeof o === "number") {
          nums.push(o);
          return;
        }
        if (Array.isArray(o)) {
          o.slice(0, 50).forEach(x => walk(x, depth + 1));
          return;
        }
        if (typeof o === "object") Object.keys(o).forEach(k => walk(o[k], depth + 1));
      };
      walk(dash, 0);
      trackingLive = nums.some(n => n > 0);
    }
    const marketCount = locations ? locations.items && locations.items.length || (Array.isArray(locations) ? locations.length : 0) : null;
    let lifecycleFieldCount = null;
    if (Array.isArray(lifecycleRules)) {
      const keys = new Set();
      lifecycleRules.forEach(r => {
        if (!r || r.active === false || !r.fieldMap) return;
        try {
          Object.keys(JSON.parse(r.fieldMap)).forEach(k => keys.add(k));
        } catch (e) {/* bad json */}
      });
      lifecycleFieldCount = {
        rules: lifecycleRules.length,
        fields: keys.size
      };
    }
    const configHasRules = !!(configRules && configRules.rules);
    this.setState({
      providers: providers || [],
      schedules: schedules || [],
      runs: runs || [],
      trackingLive,
      marketCount,
      lifecycleFieldCount,
      configVersion: configRules ? configRules.version || 0 : null,
      configHasRules
    });
    if (configHasRules) safe(dataSvc("/product/configurations/rules/preview").postAsync({}), null).then(rep => {
      if (this._alive && rep && !rep.empty) this.setState({
        configPreview: rep
      });
    });
    SEED_APPS.forEach(a => a.load().then(v => {
      if (this._alive) this.setState(st => ({
        seeded: {
          ...st.seeded,
          [a.key]: v
        }
      }));
    }).catch(() => {}));
  }
  componentWillUnmount() {
    this._alive = false;
  }
  hasProvider(name) {
    return (this.state.providers || []).some(p => p.name === name);
  }
  scheduleFor(provider) {
    return (this.state.schedules || []).find(s => s.provider === provider) || null;
  }
  /** Latest real run (source checks excluded), the same run Manage Data > Feeds reads. */
  lastRunFor(schedule) {
    return lastRealRun(schedule, this.state.runs);
  }
  cardStatus(card) {
    if (card.kind === "task") {
      const {
        marketCount,
        lifecycleFieldCount,
        configVersion,
        configPreview
      } = this.state;
      if (card.key === "markets") {
        if (marketCount === null || marketCount === undefined) return {
          label: "·",
          cls: "off"
        };
        return marketCount > 0 ? {
          label: `${marketCount} market${marketCount === 1 ? "" : "s"} defined`,
          cls: "ok"
        } : {
          label: "Not defined",
          cls: "off"
        };
      }
      if (card.key === "lifecycleFields") {
        if (!lifecycleFieldCount) return {
          label: "Not set up",
          cls: "off"
        };
        if (lifecycleFieldCount.fields > 0) return {
          label: `${lifecycleFieldCount.fields} fields mapped`,
          cls: "ok"
        };
        if (lifecycleFieldCount.rules > 0) return {
          label: "Rules active, fields not mapped",
          cls: "info"
        };
        return {
          label: "Not set up",
          cls: "off"
        };
      }
      if (card.key === "configReporting") {
        if (!this.state.configHasRules) return {
          label: "Not set up",
          cls: "off"
        };
        if (configPreview) return {
          label: `Live · ${(configPreview.styles || 0).toLocaleString()} styles · ${(configPreview.pairs || 0).toLocaleString()} pairs`,
          cls: "ok"
        };
        return {
          label: configVersion > 0 ? `Rules saved (v${configVersion})` : "Rules saved",
          cls: "info"
        };
      }
      return {
        label: "·",
        cls: "off"
      };
    }
    if (card.kind === "tracking") {
      const {
        trackingLive
      } = this.state;
      if (trackingLive === true) return {
        label: "Live",
        cls: "ok"
      };
      if (trackingLive === false) return {
        label: "No traffic yet",
        cls: "off"
      };
      return {
        label: "·",
        cls: "off"
      };
    }
    if (card.viaShopify && !this.scheduleFor(card.provider) && this.scheduleFor("Shopify")) {
      const st = this.cardStatus({
        key: "shopify",
        provider: "Shopify"
      });
      return {
        ...st,
        label: st.cls === "ok" ? "Via Shopify" + st.label.replace(/^Connected/, "") : st.label,
        via: true
      };
    }
    if (card.probe && !this.hasProvider(card.provider)) return {
      label: "Arriving soon",
      cls: "info"
    };
    const sched = this.scheduleFor(card.provider);
    if (!sched) return {
      label: "Not connected",
      cls: "off"
    };
    // Same health reading as Manage Data > Feeds (./feed-health), so a failed, empty
    // or overdue last run never shows green "Connected" here (10-12).
    const {
      state,
      run
    } = feedHealth(sched, this.state.runs);
    const freq = String(sched.frequency || "").toLowerCase();
    switch (state) {
      case "paused":
        return {
          label: "Paused",
          cls: "off"
        };
      case "running":
        return {
          label: "Running now",
          cls: "info",
          run
        };
      case "failed":
        return {
          label: "Last run failed",
          cls: "warn",
          run,
          why: "The last run didn't finish, so nothing changed. Open Feeds to run it again or check the file address."
        };
      case "empty":
        return {
          label: "Last file was empty",
          cls: "warn",
          run,
          why: "The last file had nothing in it, so nothing was refreshed. Check the export on your side, then run it again from Feeds."
        };
      case "overdue":
        return {
          label: `Not updated since ${ago(run.finishedDate || run.startedDate)}`,
          cls: "warn",
          run,
          why: "This feed hasn't run on schedule. Open Feeds to run it now."
        };
      case "ok":
        return {
          label: `Connected · ${freq} · ${ago(run.finishedDate || run.startedDate)}`,
          cls: "ok",
          run
        };
      default:
        return {
          label: `Connected · ${freq}`,
          cls: "ok"
        };
    }
  }
  renderCard(card, st) {
    const {
      contractOpen
    } = this.state;
    const shopify = !!this.scheduleFor("Shopify");
    const soon = st.label === "Arriving soon";
    const open = contractOpen === card.key;
    const go = r => this.props.history.push(r);
    return /*#__PURE__*/React.createElement(Card, {
      key: card.key,
      span: 4,
      cls: "cx-card",
      title: shopify && card.shopifyName ? card.shopifyName : card.name,
      right: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Pill, {
        tone: TONE[st.cls] || "neutral"
      }, st.label), card.required && st.cls === "off" ? /*#__PURE__*/React.createElement(Pill, {
        tone: "warning"
      }, "Start here") : null)
    }, /*#__PURE__*/React.createElement("p", {
      className: "line"
    }, clean(card.line)), /*#__PURE__*/React.createElement("div", {
      className: "chips"
    }, card.chips.map(c => /*#__PURE__*/React.createElement(Pill, {
      key: c,
      tone: "neutral"
    }, c)), card.feature && hasTenantFeature(card.feature) ? /*#__PURE__*/React.createElement(Pill, {
      tone: "success"
    }, "enabled") : null), st.why ? /*#__PURE__*/React.createElement("p", {
      className: "warnline",
      title: st.run && st.run.message || ""
    }, st.why) : null, card.key === "configReporting" && this.state.configPreview && this.state.configPreview.unpairedReason ? /*#__PURE__*/React.createElement("p", {
      className: "warnline"
    }, this.state.configPreview.unpairedReason) : null, /*#__PURE__*/React.createElement("div", {
      className: "acts"
    }, card.key === "history" && !soon ? /*#__PURE__*/React.createElement(Btn, {
      primary: true,
      small: true,
      onClick: () => this.setState({
        wizard: "history"
      })
    }, "Import history") : null, card.key === "history" && !soon ? /*#__PURE__*/React.createElement(Btn, {
      small: true,
      onClick: () => this.setState({
        wizard: "gap"
      })
    }, "Fill a tracking gap") : null, card.key === "history" && soon ? /*#__PURE__*/React.createElement("span", {
      className: "dim",
      style: {
        fontSize: 12
      }
    }, "Google Analytics import; this card activates itself when the import service is ready.") : null, card.kind === "tracking" ? /*#__PURE__*/React.createElement(Btn, {
      primary: true,
      small: true,
      onClick: () => go("/Tracking")
    }, st.label === "Live" ? "View tracking" : "Set up tracking") : null, card.manageRoute || st.via ? /*#__PURE__*/React.createElement(Btn, {
      small: true,
      onClick: () => go(card.manageRoute || "/Manage/Shopify")
    }, "Manage store") : null, card.provider && !card.probe && !card.manageRoute ? /*#__PURE__*/React.createElement(Btn, {
      small: true,
      primary: st.cls === "off" && !card.required,
      onClick: () => go("/Data")
    }, st.cls === "off" ? "Connect" : st.via ? "Add a separate feed" : "Manage feed") : null, card.kind === "task" ? /*#__PURE__*/React.createElement(Btn, {
      small: true,
      onClick: () => go(card.route)
    }, card.cta) : null, card.askStarter ? /*#__PURE__*/React.createElement("a", {
      className: "ask",
      title: `Ask Plunk: ${card.askStarter}`,
      onClick: () => askPlunk(card.askStarter)
    }, "or ask Plunk") : null, card.contract ? /*#__PURE__*/React.createElement("a", {
      className: "wa-rowlink muted",
      onClick: () => this.setState({
        contractOpen: open ? null : card.key
      })
    }, open ? "Hide contract" : "View contract") : null), open ? /*#__PURE__*/React.createElement("div", {
      className: "contract"
    }, /*#__PURE__*/React.createElement("p", null, clean(card.contract)), (card.recommended || []).map(([t, body]) => /*#__PURE__*/React.createElement("p", {
      key: t
    }, /*#__PURE__*/React.createElement("b", null, t), " \xB7 ", clean(body)))) : null);
  }
  render() {
    const {
      providers,
      wizard,
      connections,
      seeded
    } = this.state;
    const loading = providers === null;
    const shopify = !!this.scheduleFor("Shopify");
    const cards = loading ? [] : CARDS.filter(c => !(c.hideWhenAbsent && !this.hasProvider(c.provider) && !(shopify && c.viaShopify))).filter(c => !(c.needsSales && !this.scheduleFor("SalesTransaction") && !shopify)).filter(c => shopify ? !(c.hideForShopify && !this.scheduleFor(c.provider)) : !c.shopifyOnly);
    const statuses = {};
    cards.forEach(c => {
      statuses[c.key] = this.cardStatus(c);
    });
    const sources = cards.filter(c => c.kind !== "task");
    const tasks = cards.filter(c => c.kind === "task");
    const countable = sources.filter(c => !c.oneTime && !c.optional && !(statuses[c.key] && statuses[c.key].via));
    const connected = countable.filter(c => statuses[c.key].cls === "ok" || c.kind === "tracking" && statuses[c.key].label === "Live").length;
    const nextUp = countable.find(c => statuses[c.key].cls === "off" && c.kind !== "tracking");
    const tr = statuses.tracking;
    const connOk = connections ? (connections.connectors || []).filter(c => String(c.status || "").toLowerCase() === "connected").length + (connections.social || []).filter(a => String(a.status || "").toLowerCase() === "connected").length : null;
    const seededCount = Object.values(seeded).filter(Boolean).length;
    const tiles = [{
      l: "Feeds",
      v: loading ? "…" : `${connected} of ${countable.length}`,
      s: loading ? "" : nextUp ? `Next: ${nextUp.name.toLowerCase()}` : "every required source is in"
    }, {
      l: "Tracking",
      v: loading ? "…" : tr ? tr.label : "·",
      s: "traffic this month"
    }, {
      l: "Markets",
      v: loading ? "…" : this.state.marketCount == null ? "·" : String(this.state.marketCount),
      s: "defined for geo views"
    }, {
      l: "Connections",
      v: connOk == null ? "…" : String(connOk),
      s: "provider accounts connected"
    }];
    const tenantId = (account.get() || {}).tenantId || "";
    return /*#__PURE__*/React.createElement(DataShell, {
      active: "/Data/Connect",
      subtitle: "Connect your data: the feeds, tracking and accounts that turn Plunk's sections on",
      chips: loading ? [] : [{
        k: "Connected",
        v: `${connected} of ${countable.length}`
      }],
      actions: /*#__PURE__*/React.createElement(Btn, {
        primary: true,
        onClick: () => askPlunk("Help me finish setting up my workspace")
      }, "Ask Plunk")
    }, wizard ? /*#__PURE__*/React.createElement(ImportWizard, {
      initialMode: wizard === "gap" ? "gap" : wizard === "history" ? "history" : null,
      onClose: () => this.setState({
        wizard: false
      })
    }) : null, /*#__PURE__*/React.createElement(Ribbon, {
      tiles: tiles,
      comparing: null,
      cols: 4,
      loading: loading
    }), !loading ? /*#__PURE__*/React.createElement(Notice, null, /*#__PURE__*/React.createElement("span", {
      className: "cx-progress"
    }, /*#__PURE__*/React.createElement("i", {
      style: {
        width: `${Math.round(connected / Math.max(1, countable.length) * 100)}%`
      }
    })), /*#__PURE__*/React.createElement("span", {
      className: "grow"
    }, nextUp ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("b", null, "Next up:"), " your ", nextUp.name.toLowerCase(), " unlocks ", nextUp.chips.slice(0, 2).join(" and "), ". ") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("b", null, "Sources are in."), " "), "Ask Plunk can do most of this setup with you: events script, target markets, follow-up emails, a first report, seeding the apps. A person can walk you through it too."), /*#__PURE__*/React.createElement("a", {
      className: "wa-rowlink muted",
      href: `mailto:support@plunk.io?subject=${encodeURIComponent("Onboarding walkthrough request")}&body=${encodeURIComponent("We'd like a guided walkthrough of workspace setup.\n\nWorkspace: " + tenantId)}`
    }, "Request a walkthrough")) : null, /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, loading ? /*#__PURE__*/React.createElement(Card, {
      span: 12
    }, /*#__PURE__*/React.createElement(Empty, null, "Checking your feeds and connections\u2026")) : null, sources.length ? /*#__PURE__*/React.createElement("div", {
      className: "wa-group"
    }, "Sources") : null, sources.map(c => this.renderCard(c, statuses[c.key])), tasks.length ? /*#__PURE__*/React.createElement("div", {
      className: "wa-group"
    }, "Setup") : null, tasks.map(c => this.renderCard(c, statuses[c.key])), !loading ? /*#__PURE__*/React.createElement("div", {
      className: "wa-group"
    }, "Apps and accounts") : null, !loading ? /*#__PURE__*/React.createElement(Card, {
      span: 6,
      title: "Seed your apps",
      hint: `${seededCount} of ${SEED_APPS.length} seeded`,
      cls: "cx-card"
    }, /*#__PURE__*/React.createElement("p", {
      className: "line"
    }, "The six apps start empty. Ask Plunk suggests starting content from your business, you approve, it seeds. Report runs that spend credits are confirmed one by one."), /*#__PURE__*/React.createElement("div", {
      className: "cx-seed"
    }, SEED_APPS.map(a => /*#__PURE__*/React.createElement("span", {
      key: a.key
    }, /*#__PURE__*/React.createElement("span", {
      className: "material-icons"
    }, a.icon), /*#__PURE__*/React.createElement("span", {
      style: {
        minWidth: 0
      }
    }, a.name, " ", /*#__PURE__*/React.createElement("span", {
      className: "st" + (seeded[a.key] ? " ok" : "")
    }, "\xB7 ", seeded[a.key] || "empty")), !seeded[a.key] ? /*#__PURE__*/React.createElement("a", {
      title: `Ask Plunk: ${a.starter}`,
      onClick: () => askPlunk(a.starter)
    }, "seed") : null))), /*#__PURE__*/React.createElement("div", {
      className: "acts"
    }, /*#__PURE__*/React.createElement(Btn, {
      primary: true,
      small: true,
      onClick: () => askPlunk("Help me seed my apps")
    }, "Ask Plunk to seed"), /*#__PURE__*/React.createElement(Btn, {
      small: true,
      onClick: () => this.props.history.push("/Apps")
    }, "Open Apps"))) : null, !loading && !this.state.pointerDismissed ? /*#__PURE__*/React.createElement(Card, {
      span: 6,
      title: "Meet Ask Plunk",
      cls: "cx-card",
      right: /*#__PURE__*/React.createElement("a", {
        className: "wa-rowlink muted",
        onClick: () => {
          try {
            localStorage.setItem("plunk-onboarding-askplunk-pointer", "1");
          } catch (e) {/* private */}
          this.setState({
            pointerDismissed: true
          });
        }
      }, "Dismiss")
    }, /*#__PURE__*/React.createElement("p", {
      className: "line"
    }, "The chat button answers questions about your own data from day one, and it can run setup itself: it checks what is done, then generates the events script, creates markets, drafts email automations and reports, and seeds your apps. Everything lands as a draft or config for you to approve. Connecting accounts and uploading files stay yours."), /*#__PURE__*/React.createElement("div", {
      className: "acts"
    }, /*#__PURE__*/React.createElement(Btn, {
      small: true,
      onClick: () => askPlunk("What can you set up for me?")
    }, "Open Ask Plunk"))) : null, !loading ? /*#__PURE__*/React.createElement(Connections, {
      history: this.props.history,
      onLoaded: c => this.setState({
        connections: c
      })
    }) : null));
  }
}
const Routable = withRouter(ConnectView);
export class ConnectViewComponent extends React.Component {
  render() {
    return /*#__PURE__*/React.createElement(Routable, {
      params: this.props.params
    });
  }
}
export default ConnectViewComponent;